import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import type { AppLocale } from "@/i18n/locales";
import { dateRangeAtom } from "@/state/filters.state";
import { localeAtom } from "@/state/locale.state";
import { buildDatePresets } from "@/utils/date-presets.utils.ts";
import { endOfDay } from "@/utils/date.utils";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import type { Locale } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import { useAtom, useAtomValue } from "jotai";
import type { DateRange } from "react-day-picker";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

const DATE_FNS_LOCALE: Record<AppLocale, Locale> = {
  en: enUS,
  fr: fr,
};

type DatePickerProps = {
  variant?: "sidebar" | "mobile";
  onDone?: () => void;
};

export function DatePicker({ variant = "sidebar", onDone }: DatePickerProps) {
  const [range, setRange] = useAtom(dateRangeAtom);

  const { i18n } = useLingui();
  const appLocale = useAtomValue(localeAtom);
  const locale = DATE_FNS_LOCALE[appLocale];

  const isMobile = variant === "mobile";

  const presets = buildDatePresets();

  const onCalendarSelect = (r: DateRange | undefined) => {
    let newRange: DateRange = { from: undefined };
    if (r) {
      newRange.from = r.from;
      if (r.to) {
        newRange.to = endOfDay(r.to);
      }
    }
    setRange(newRange);
  };

  const PresetGroup = ({
    title,
    items,
  }: {
    title: string;
    items: ReturnType<typeof buildDatePresets>[keyof ReturnType<
      typeof buildDatePresets
    >];
  }) => (
    <div>
      <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
        {title}
      </div>
      <div className="space-y-0.5">
        {items.map((p) => (
          <div key={p.key}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="justify-start"
              onClick={() => {
                const r = p.getRange();
                setRange(r ?? { from: undefined });
                onDone?.();
              }}
            >
              {i18n._(p.label)}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );

  // --- Render ----------------------------------------------------------------

  return isMobile ? (
    <Tabs defaultValue="presets" className="flex h-full w-full flex-col">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-background">
        <TabsList className="w-full">
          <TabsTrigger value="presets" className="flex-1">
            {t`Presets`}
          </TabsTrigger>
          <TabsTrigger value="calendar" className="flex-1">
            {t`Calendar`}
          </TabsTrigger>
        </TabsList>
      </div>

      {/* Scrollable middle */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <TabsContent value="presets" className="mt-3">
          <div className="grid grid-cols-1 gap-2 px-1 pb-3">
            <PresetGroup title={t`Quick`} items={presets.rolling} />
            <PresetGroup title={t`Months`} items={presets.months} />
            <PresetGroup title={t`Years`} items={presets.years} />
          </div>
        </TabsContent>

        <TabsContent value="calendar" className="mt-3">
          <div className="min-w-0 px-1 pb-3">
            <Calendar
              locale={locale}
              mode="range"
              selected={range}
              onSelect={onCalendarSelect}
              numberOfMonths={1}
              className="w-full"
            />
          </div>
        </TabsContent>
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 z-10 bg-background pb-[var(--safe-bottom)]">
        <Separator className="my-2" />
        <div className="flex items-center justify-between px-2 pb-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setRange({ from: undefined })}
          >
            {t`Clear dates`}
          </Button>

          <Button variant="default" size="sm" onClick={onDone}>
            {t`Done`}
          </Button>
        </div>
      </div>
    </Tabs>
  ) : (
    <div className="flex gap-6">
      {/* Presets */}
      <div className="min-h-0 overflow-y-auto">
        <div className="grid grid-cols-1 gap-2 md:block md:space-y-3">
          <PresetGroup title={t`Quick`} items={presets.rolling} />
          <PresetGroup title={t`Months`} items={presets.months} />
          <PresetGroup title={t`Years`} items={presets.years} />
        </div>
      </div>

      <Separator className="hidden md:block" orientation="vertical" />

      {/* Calendar */}
      <div className="flex flex-col min-w-0">
        <Calendar
          locale={locale}
          mode="range"
          selected={range}
          onSelect={onCalendarSelect}
          numberOfMonths={1}
        />

        <Separator className="my-2" />

        <div className="flex-1" />

        <div className="flex items-center justify-between px-2 pb-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setRange({ from: undefined })}
          >
            {t`Clear dates`}
          </Button>

          <Button variant="default" size="sm" onClick={onDone}>
            {t`Done`}
          </Button>
        </div>
      </div>
    </div>
  );
}
